// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

// Generic API request function
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const config = {
    headers: getAuthHeaders(),
    ...options
  };

  if (config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body);
  }

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

// Authentication API
export const authAPI = {
  register: (userData) => 
    apiRequest('/auth/register', {
      method: 'POST',
      body: userData
    }),

  login: (credentials) => 
    apiRequest('/auth/login', {
      method: 'POST',
      body: credentials
    }),

  getCurrentUser: () => 
    apiRequest('/auth/me'),

  updateProfile: (profileData) => 
    apiRequest('/auth/profile', {
      method: 'PUT',
      body: profileData
    }),

  updatePassword: (passwordData) => 
    apiRequest('/auth/updatepassword', {
      method: 'PUT',
      body: passwordData
    }),

  getVerifiers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/auth/verifiers${queryString ? `?${queryString}` : ''}`);
  },

  getVerifierById: (id) => 
    apiRequest(`/auth/verifiers/${id}`),

  updateVerifierInfo: (verifierData) => 
    apiRequest('/auth/verifier-info', {
      method: 'PUT',
      body: verifierData
    }),

  updateBuyerInfo: (buyerData) => 
    apiRequest('/auth/buyer-info', {
      method: 'PUT',
      body: buyerData
    })
};

// Projects API
export const projectsAPI = {
  getProjects: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/projects${queryString ? `?${queryString}` : ''}`);
  },

  getProject: (id) => 
    apiRequest(`/projects/${id}`),

  createProject: (projectData) => 
    apiRequest('/projects', {
      method: 'POST',
      body: projectData
    }),

  updateProject: (id, projectData) => 
    apiRequest(`/projects/${id}`, {
      method: 'PUT',
      body: projectData
    }),

  deleteProject: (id) => 
    apiRequest(`/projects/${id}`, {
      method: 'DELETE'
    }),

  getMyProjects: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/projects/my/projects${queryString ? `?${queryString}` : ''}`);
  },

  submitProject: (id) => 
    apiRequest(`/projects/${id}/submit`, {
      method: 'POST'
    }),

  reviewProject: (id, reviewData) => 
    apiRequest(`/projects/${id}/review`, {
      method: 'PUT',
      body: reviewData
    }),

  getProjectStats: () => 
    apiRequest('/projects/stats/overview')
};

// Verification API
export const verificationAPI = {
  getRequests: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/verification/requests${queryString ? `?${queryString}` : ''}`);
  },

  getRequest: (id) => 
    apiRequest(`/verification/requests/${id}`),

  createRequest: (requestData) => 
    apiRequest('/verification/requests', {
      method: 'POST',
      body: requestData
    }),

  updateRequest: (id, requestData) => 
    apiRequest(`/verification/requests/${id}`, {
      method: 'PUT',
      body: requestData
    }),

  getMyRequests: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/verification/my-requests${queryString ? `?${queryString}` : ''}`);
  },

  getProposals: (requestId) => 
    apiRequest(`/verification/requests/${requestId}/proposals`),

  submitProposal: (requestId, proposalData) => 
    apiRequest(`/verification/requests/${requestId}/proposals`, {
      method: 'POST',
      body: proposalData
    }),

  getMyProposals: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/verification/my-proposals${queryString ? `?${queryString}` : ''}`);
  },

  updateProposal: (id, proposalData) => 
    apiRequest(`/verification/proposals/${id}`, {
      method: 'PUT',
      body: proposalData
    }),

  acceptProposal: (id, reviewData) => 
    apiRequest(`/verification/proposals/${id}/accept`, {
      method: 'POST',
      body: reviewData
    }),

  rejectProposal: (id, reviewData) => 
    apiRequest(`/verification/proposals/${id}/reject`, {
      method: 'POST',
      body: reviewData
    }),

  getDashboard: () => 
    apiRequest('/verification/dashboard'),

  getStats: () => 
    apiRequest('/verification/stats')
};

// Transactions API
export const transactionsAPI = {
  purchaseCredits: (purchaseData) => 
    apiRequest('/transactions/purchase', {
      method: 'POST',
      body: purchaseData
    }),

  getMyTransactions: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/transactions/my${queryString ? `?${queryString}` : ''}`);
  },

  getTransaction: (id) => 
    apiRequest(`/transactions/${id}`),

  getMyCertificates: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/transactions/certificates/my${queryString ? `?${queryString}` : ''}`);
  },

  retireCredits: (id, retirementData) => 
    apiRequest(`/transactions/${id}/retire`, {
      method: 'POST',
      body: retirementData
    }),

  getTransactionStats: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/transactions/stats/overview${queryString ? `?${queryString}` : ''}`);
  },

  processRefund: (id, refundData) => 
    apiRequest(`/transactions/${id}/refund`, {
      method: 'POST',
      body: refundData
    })
};

// Health and System API
export const systemAPI = {
  healthCheck: () => 
    fetch(`${API_BASE_URL.replace('/api', '')}/health`).then(res => res.json()),

  getAPIInfo: () => 
    apiRequest('/')
};

// Auth utilities
export const authUtils = {
  setToken: (token) => {
    localStorage.setItem('authToken', token);
  },

  removeToken: () => {
    localStorage.removeItem('authToken');
  },

  isAuthenticated: () => {
    return !!getAuthToken();
  },

  getUserFromToken: () => {
    const token = getAuthToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }
};

// Error handling utilities
export const handleAPIError = (error) => {
  if (error.message.includes('401') || error.message.includes('unauthorized')) {
    // Token expired or invalid
    authUtils.removeToken();
    window.location.href = '/auth';
    return;
  }

  // Generic error handling
  console.error('API Error:', error);
  throw error;
};

// Default export
const api = {
  auth: authAPI,
  projects: projectsAPI,
  verification: verificationAPI,
  transactions: transactionsAPI,
  system: systemAPI,
  utils: authUtils
};

export default api; 